import { useCallback, useEffect, useState } from 'react';
import { IManagingWindowsServiceInstance } from '../../api/models';
import { EventType } from '../../constants/EventType';
import { WindowsServiceAction } from '../../constants/WindowsServiceAction';
import { WindowsServiceStartType } from '../../constants/WindowsServiceStartType';
import {
    ServerApplicationClientEventSummaryHandler,
    ServerApplicationClientEventSummaryHandlerArgs,
} from '../ApplicationServerManagement/ServerApplicationClientEventSummaryHandler';
import useRefedProp from '../../hooks/useRefedProp';
import { Permission } from '../../permissions/permission';
import { UserPrincipal } from '../../permissions/userPrincipal';
import { ServerApplicationClientEventSummary } from '../../signalr/models';
import signalREventsClient from '../../signalr/SignalREventsClient';
import { InfoModal } from '../Shared/InfoModal';
import { ActionButton } from '../UIExtentions/Buttons';
import { ServerWindowsServiceDetails } from '../WindowsServiceInstallInfo/WindowsServiceInstallInfoView';
import { ApplicationUninstall } from '../ApplicationInstallation/ApplicationUninstall';

export interface WindowsServicesServerRowActionsProps {
    serverApp: IManagingWindowsServiceInstance;
    userPrincipal: UserPrincipal;
    serverUserPrincipal: UserPrincipal;
    onClickCallback: (action: WindowsServiceAction, startType?: WindowsServiceStartType) => Promise<void>;
    onTimeoutCallback: (data: ServerApplicationClientEventSummary) => void;
}

export function WindowsServicesServerRowActions(props: WindowsServicesServerRowActionsProps) {
    const [showInfo, setShowInfo] = useState(false);
    const [startSpinning, setStartSpinning] = useState(false);
    const [stopSpinning, setStopSpinning] = useState(false);
    const [restartSpinning, setRestartSpinning] = useState(false);
    const [killSpinning, setKillSpinning] = useState(false);
    const [changeStartupTypeToManualSpinning, setChangeStartupTypeToManualSpinning] = useState(false);
    const [changeStartupTypeToAutomaticSpinning, setChangeStartupTypeToAutomaticSpinning] = useState(false);
    const [changeStartupTypeToDisabledSpinning, setChangeStartupTypeToDisabledSpinning] = useState(false);
    const [changeStartupTypeToManualDisabled, setChangeStartupTypeToManualDisabled] = useState(false);
    const [changeStartupTypeToAutomaticDisabled, setChangeStartupTypeToAutomaticDisabled] = useState(false);
    const [changeStartupTypeToDisabledDisabled, setChangeStartupTypeToDisabledDisabled] = useState(false);
    const [refreshSpinning, setRefreshSpinning] = useState(false);
    const onTimeoutCallbackRef = useRefedProp(props.onTimeoutCallback);

    const serverApp = props.serverApp;
    const userPrincipal = props.userPrincipal;
    const serverUserPrincipal = props.serverUserPrincipal;
    const onClickCallback = props.onClickCallback;

    const getEnableState = function (permission: Permission): boolean {
        return userPrincipal.hasPermission(permission) || serverUserPrincipal?.hasPermission(permission);
    };

    const setChangeStartupTypesEnabled = async function (enabled: boolean) {
        setChangeStartupTypeToManualDisabled(!enabled);
        setChangeStartupTypeToAutomaticDisabled(!enabled);
        setChangeStartupTypeToDisabledDisabled(!enabled);
    };

    const setChangeStartupTypesSpinning = async function (spinning: boolean) {
        setChangeStartupTypeToManualSpinning(spinning);
        setChangeStartupTypeToAutomaticSpinning(spinning);
        setChangeStartupTypeToDisabledSpinning(spinning);
    };

    const serviceChangeStartupTypeTriggerEventHandler = useCallback((event: ServerApplicationClientEventSummary) => {
        switch (event.typeDetails as WindowsServiceStartType) {
            case WindowsServiceStartType.Manual:
                setChangeStartupTypeToManualSpinning(true);
                break;
            case WindowsServiceStartType.Automatic:
                setChangeStartupTypeToAutomaticSpinning(true);
                break;
            case WindowsServiceStartType.Disabled:
                setChangeStartupTypeToDisabledSpinning(true);
                break;
            default:
                break;
        }
        setChangeStartupTypesEnabled(false);
    }, []);

    const serviceChangeStartupTypeEventHandler = useCallback(() => {
        setChangeStartupTypesSpinning(false);
        setChangeStartupTypesEnabled(true);
    }, []);

    useEffect(() => {
        const serviceStartHandler = new ServerApplicationClientEventSummaryHandler(serverApp.serverName, {
            addTypes: [EventType.WindowsServiceStartTriggered],
            failedTypes: [EventType.WindowsServiceStartFailed],
            successTypes: [EventType.WindowsServiceStartSucceeded],
            emptyCallback: () => setStartSpinning(false),
            addCallback: () => setStartSpinning(true),
            timeoutCallback: (data) => onTimeoutCallbackRef.current?.(data),
        } as ServerApplicationClientEventSummaryHandlerArgs);

        const serviceStopHandler = new ServerApplicationClientEventSummaryHandler(serverApp.serverName, {
            addTypes: [EventType.WindowsServiceStopTriggered],
            failedTypes: [EventType.WindowsServiceStopFailed],
            successTypes: [EventType.WindowsServiceStopSucceeded],
            emptyCallback: () => setStopSpinning(false),
            addCallback: () => setStopSpinning(true),
            timeoutCallback: (data) => onTimeoutCallbackRef.current?.(data),
        } as ServerApplicationClientEventSummaryHandlerArgs);

        const serviceRestartHandler = new ServerApplicationClientEventSummaryHandler(serverApp.serverName, {
            addTypes: [EventType.WindowsServiceRestartTriggered],
            failedTypes: [EventType.WindowsServiceRestartFailed],
            successTypes: [EventType.WindowsServiceRestartSucceeded],
            emptyCallback: () => setRestartSpinning(false),
            addCallback: () => setRestartSpinning(true),
            timeoutCallback: (data) => onTimeoutCallbackRef.current?.(data),
        } as ServerApplicationClientEventSummaryHandlerArgs);

        const serviceKillHandler = new ServerApplicationClientEventSummaryHandler(serverApp.serverName, {
            addTypes: [EventType.WindowsServiceKillTriggered],
            failedTypes: [EventType.WindowsServiceKillFailed],
            successTypes: [EventType.WindowsServiceKillSucceeded],
            emptyCallback: () => setKillSpinning(false),
            addCallback: () => setKillSpinning(true),
            timeoutCallback: (data) => onTimeoutCallbackRef.current?.(data),
        } as ServerApplicationClientEventSummaryHandlerArgs);

        const serviceChangeStartupTypeHandler = new ServerApplicationClientEventSummaryHandler(serverApp.serverName, {
            addTypes: [EventType.WindowsServiceChangeStartTypeTriggered],
            failedTypes: [EventType.WindowsServiceChangeStartTypeFailed],
            successTypes: [EventType.WindowsServiceChangeStartTypeSucceeded],
            emptyCallback: serviceChangeStartupTypeEventHandler,
            addCallback: serviceChangeStartupTypeTriggerEventHandler,
            timeoutCallback: (data) => onTimeoutCallbackRef.current?.(data),
        } as ServerApplicationClientEventSummaryHandlerArgs);

        const serviceRefreshHandler = new ServerApplicationClientEventSummaryHandler(serverApp.serverName, {
            addTypes: [EventType.WindowsServiceRefreshTriggered],
            failedTypes: [EventType.WindowsServiceRefreshFailed],
            successTypes: [EventType.WindowsServiceRefreshSucceeded],
            emptyCallback: () => setRefreshSpinning(false),
            addCallback: () => setRefreshSpinning(true),
            timeoutCallback: (data) => onTimeoutCallbackRef.current?.(data),
        } as ServerApplicationClientEventSummaryHandlerArgs);

        function isForMe(data: ServerApplicationClientEventSummary) {
            return data.targetApplication === serverApp.name && data.targetServer === serverApp.serverName;
        }

        signalREventsClient.onWindowsServiceEvent.setHook(`ServerRow_${serverApp.name}_${serverApp.serverName}`, (data) => {
            if (isForMe(data)) {
                serviceStartHandler.handleItem(data);
                serviceStopHandler.handleItem(data);
                serviceRestartHandler.handleItem(data);
                serviceKillHandler.handleItem(data);
                serviceChangeStartupTypeHandler.handleItem(data);
                serviceRefreshHandler.handleItem(data);
            }
        });
    }, [onTimeoutCallbackRef, serverApp.name, serverApp.serverName, serviceChangeStartupTypeEventHandler, serviceChangeStartupTypeTriggerEventHandler]);

    const infoModalTitle = `${serverApp.name} - ${serverApp.serverName}`;

    return (
        <>
            <td>
                <ActionButton
                    disabled={!getEnableState(Permission.WindowsService_Refresh)}
                    spinning={refreshSpinning}
                    icon="fa-refresh"
                    variant="btn-dark-blue"
                    title="Refresh"
                    onClick={async () => await onClickCallback(WindowsServiceAction.Refresh)}
                />
            </td>
            <td>
                <ActionButton
                    disabled={!getEnableState(Permission.WindowsService_Start)}
                    spinning={startSpinning}
                    icon="fa-play"
                    variant="btn-green"
                    title="Start"
                    onClick={async () => await onClickCallback(WindowsServiceAction.Start)}
                />
                <ActionButton
                    disabled={!getEnableState(Permission.WindowsService_Stop)}
                    spinning={stopSpinning}
                    icon="fa-stop"
                    variant="btn-red"
                    title="Stop"
                    onClick={async () => await onClickCallback(WindowsServiceAction.Stop)}
                />
                <ActionButton
                    disabled={!getEnableState(Permission.WindowsService_Restart)}
                    spinning={restartSpinning}
                    icon="fa-step-forward"
                    variant="btn-blue"
                    title="Restart"
                    onClick={async () => await onClickCallback(WindowsServiceAction.Restart)}
                />
                <ActionButton
                    disabled={!getEnableState(Permission.WindowsService_Kill)}
                    spinning={killSpinning}
                    icon="fa-bomb"
                    variant="btn-dark-grey"
                    title="Kill"
                    onClick={async () => await onClickCallback(WindowsServiceAction.Kill)}
                />
                |
                <ActionButton
                    disabled={!getEnableState(Permission.WindowsService_ChangeStartupType) || changeStartupTypeToManualDisabled}
                    spinning={changeStartupTypeToManualSpinning}
                    icon="fa-hand-o-up"
                    variant="btn-grey-blue"
                    title="Set Manual"
                    onClick={async () => await onClickCallback(WindowsServiceAction.ChangeStartupType, WindowsServiceStartType.Manual)}
                />
                <ActionButton
                    disabled={!getEnableState(Permission.WindowsService_ChangeStartupType) || changeStartupTypeToAutomaticDisabled}
                    spinning={changeStartupTypeToAutomaticSpinning}
                    icon="fa-bolt"
                    variant="btn-orange"
                    title="Set Auto"
                    onClick={async () => await onClickCallback(WindowsServiceAction.ChangeStartupType, WindowsServiceStartType.Automatic)}
                />
                <ActionButton
                    disabled={!getEnableState(Permission.WindowsService_ChangeStartupType) || changeStartupTypeToDisabledDisabled}
                    spinning={changeStartupTypeToDisabledSpinning}
                    icon="fa-ban"
                    variant="btn-dark-grey"
                    title="Set Disabled"
                    onClick={async () => await onClickCallback(WindowsServiceAction.ChangeStartupType, WindowsServiceStartType.Disabled)}
                />
                |
                <ActionButton icon="fa-info-circle" variant="btn-dark-blue" title="Info" onClick={() => setShowInfo(true)} />
                <InfoModal title={infoModalTitle} show={showInfo} onHide={() => setShowInfo(false)}>
                    <ServerWindowsServiceDetails serverApp={serverApp}></ServerWindowsServiceDetails>
                </InfoModal>
                |
                <ApplicationUninstall applicationName={serverApp.name} server={serverApp.serverName} onClickCallback={props.onClickCallback} userPrincipal={userPrincipal} />
            </td>
        </>
    );
}
